import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Container,
  SimpleGrid,
  useColorModeValue,
  Image,
  Center,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertDescription,
  Switch,
} from '@chakra-ui/react';
import { FiUsers, FiUser, FiHelpCircle, FiStar } from 'react-icons/fi';

import { Card, Button } from '@grcs/components';

import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { UserRole } from '@grcs/types/UserRole';
import csrf_logo from '@grcs/images/csrf_logo.png';
import background_image from '@grcs/images/grcs_homepage.jpg';

export const EntryPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const {
    isOpen: isHelpOpen,
    onOpen: onHelpOpen,
    onClose: onHelpClose,
  } = useDisclosure();
  const [loginUser] = useLocalStorage('user', {});
  const [storedLanguage, setLanguage] = useLocalStorage('auth_language', 'en');
  const language = storedLanguage as 'en' | 'zh';

  // Theme colors
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, teal.50, green.50)',
    'linear(to-br, blue.900, teal.900, gray.900)',
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const overlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800');
  const isChinese = language === 'zh';
  const copy = {
    title: '泓河开卷™',
    languageLabel: isChinese ? '中文' : 'English',
    student: isChinese ? '学生' : 'Student',
    teacher: isChinese ? '教师' : 'Teacher',
    studentAuth: isChinese ? '用户名和密码' : 'Username & Password',
    teacherAuth: isChinese ? '邮箱和密码' : 'Email & Password',
    helpPrompt: isChinese ? '登录帮助？' : 'Need help logging in?',
    helpCta: isChinese ? '查看帮助中心' : 'View Help Center',
    helpTitle: isChinese ? '帮助与支持' : 'Help & Support',
    students: isChinese ? '学生' : 'Students',
    teachers: isChinese ? '教师' : 'Teachers',
    studentCredentialsLabel: isChinese ? '账号信息：' : 'Credentials:',
    studentCredentialsText: isChinese
      ? '用户名和密码由任课老师提供'
      : 'Your username and password are provided by your teacher',
    studentPasswordLabel: isChinese ? '密码格式：' : 'Password Format:',
    studentPasswordText: isChinese
      ? '通常为出生日期（YYYYMMDD 格式）'
      : 'Usually your date of birth (YYYYMMDD format)',
    studentAssistanceLabel: isChinese ? '需要帮助？' : 'Need Assistance?',
    studentAssistanceText: isChinese
      ? '请联系老师协助重置登录信息'
      : 'Contact your teacher for help resetting your login',
    teacherLoginLabel: isChinese ? '登录：' : 'Login:',
    teacherLoginText: isChinese
      ? '请使用邮箱地址和密码登录'
      : 'Use your email address and password',
    teacherForgotLabel: isChinese ? '忘记密码？' : 'Forgot Password?',
    teacherForgotText: isChinese
      ? '请联系系统管理员重置密码'
      : 'Contact the system administrator for a password reset',
    teacherAccountLabel: isChinese ? '账户创建：' : 'Account Creation:',
    teacherAccountText: isChinese
      ? '账户必须先由管理员创建'
      : 'Your account must be created by an administrator first',
    footerCopyright: isChinese
      ? '© 2024 泓河开卷™ by Grand River Chinese School'
      : '© 2024 泓河开卷™ by Grand River Chinese School',
  };

  const signin = useCallback(
    (sign_role: UserRole) => {
      if (sign_role === UserRole.ADMIN) {
        navigate('/admin', { replace: true });
      } else if (sign_role === UserRole.TEACHER) {
        navigate('/teacher', { replace: true });
      } else if (sign_role === UserRole.STUDENT) {
        navigate('/student', { replace: true });
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (loginUser.isLogin && loginUser.remember) {
      signin(loginUser.role);
    }
  }, [loginUser, signin]);

  const handleRoleSelection = (role: 'student' | 'teacher' | 'admin') => {
    if (role === 'student') {
      navigate('/auth/student-login');
    } else {
      navigate('/auth/admin-login');
    }
  };

  return (
    <Box
      h="100dvh"
      bgGradient={bgGradient}
      position="relative"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Hero Section */}
      <Box
        h="95%"
        display="flex"
        justifyContent="center"
        backgroundImage={`url(${background_image})`}
        backgroundSize="cover"
        backgroundPosition="center"
      >
        <Container
          maxW="6xl"
          w={{ base: '50%', md: '50%', lg: '40%' }}
          h="100%"
          px={{ base: 4, md: 8 }}
          display="flex"
          flexDirection="column"
        >
          <Box
            flex="0 0 auto"
            minH={{ base: '120px', md: '20%' }}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {/* Header */}
            <VStack spacing={3} textAlign="center">
              <Box
                display="flex"
                flexDirection={{ base: 'column', md: 'row' }}
                alignItems="center"
                justifyContent="center"
                gap={{ base: 3, md: 6 }}
              >
                <Image
                  src={csrf_logo}
                  alt="School Logo"
                  h={{ base: '48px', md: '60px' }}
                />
                <VStack spacing={0} align={{ base: 'center', md: 'start' }}>
                  <Text
                    fontSize="4xl"
                    fontWeight="bold"
                    color="black.600"
                    display="inline-block"
                    whiteSpace="nowrap"
                    lineHeight="1"
                    flexShrink={0}
                  >
                    {copy.title}
                  </Text>
                </VStack>
              </Box>
              <HStack spacing={3}>
                <Text fontSize="sm" fontWeight="600" color="black.700">
                  English
                </Text>
                <Switch
                  colorScheme="green"
                  isChecked={isChinese}
                  onChange={(event) =>
                    setLanguage(event.target.checked ? 'zh' : 'en')
                  }
                  aria-label="Toggle language between English and Chinese"
                />
                <Text fontSize="sm" fontWeight="600" color="black.700">
                  中文
                </Text>
              </HStack>
            </VStack>
          </Box>

          <Box
            flex="1"
            minH={0}
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            mt={4}
          >
            {/* Login Options */}
            <Card
              w="100%"
              maxH="100%"
              overflowY="auto"
              bg={cardBg}
              shadow="2xl"
              borderRadius="2xl"
              borderWidth="1px"
              borderColor="gray.200"
              px={{ base: 6, md: 10 }}
              py={{ base: 6, md: 8 }}
            >
              <VStack spacing={7}>
                <SimpleGrid
                  columns={{ base: 1, md: 2 }}
                  spacing={6}
                  width="100%"
                >
                  {/* Student Login */}
                  <Card
                    variant="outline"
                    cursor="pointer"
                    borderWidth="2px"
                    p={6}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-6px)',
                      shadow: '2xl',
                      borderColor: 'blue.400',
                      borderWidth: '2px',
                    }}
                    onClick={() => handleRoleSelection('student')}
                  >
                    <VStack spacing={4} align="center">
                      <Box p={4} borderRadius="full" bg="blue.100" shadow="md">
                        <FiUsers size={40} color="#3182ce" />
                      </Box>
                      <VStack spacing={2} textAlign="center">
                        <Text
                          fontSize={{ base: 'lg', md: 'xl' }}
                          fontWeight="bold"
                          color="black.700"
                        >
                          {copy.student}
                        </Text>
                        <Badge
                          colorScheme="blue"
                          variant="subtle"
                          fontSize="xs"
                        >
                          {copy.studentAuth}
                        </Badge>
                      </VStack>
                    </VStack>
                  </Card>

                  {/* Staff Login (Teachers & Administrators) */}
                  <Card
                    variant="outline"
                    borderWidth="2px"
                    cursor="pointer"
                    p={6}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-6px)',
                      shadow: '2xl',
                      borderColor: 'green.400',
                      borderWidth: '2px',
                    }}
                    onClick={() => handleRoleSelection('teacher')}
                  >
                    <VStack spacing={4} align="center">
                      <Box p={4} borderRadius="full" bg="green.100" shadow="md">
                        <FiUser size={40} color="#22863a" />
                      </Box>
                      <VStack spacing={2} textAlign="center">
                        <Text
                          fontSize={{ base: 'lg', md: 'xl' }}
                          fontWeight="bold"
                          color="black.700"
                        >
                          {copy.teacher}
                        </Text>
                        <Badge
                          colorScheme="green"
                          variant="subtle"
                          fontSize="xs"
                        >
                          {copy.teacherAuth}
                        </Badge>
                      </VStack>
                    </VStack>
                  </Card>
                </SimpleGrid>

                {/* Divider */}
                <Box width="100%" h="1px" bg="gray.200" />

                {/* Help Button */}
                <VStack spacing={3} width="100%" align="center">
                  <HStack spacing={2} justify="center">
                    <FiHelpCircle color="gray" size={18} />
                    <Text fontSize="sm" color="gray.600" fontWeight="500">
                      {copy.helpPrompt}
                    </Text>
                  </HStack>
                  <Button
                    variant="outline"
                    size="md"
                    borderRadius="8px"
                    fontSize="sm"
                    fontWeight="500"
                    onClick={onHelpOpen}
                    _hover={{
                      bg: 'blue.50',
                      borderColor: 'blue.400',
                    }}
                  >
                    {copy.helpCta}
                  </Button>
                </VStack>
              </VStack>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Help Modal */}
      <Modal isOpen={isHelpOpen} onClose={onHelpClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="2xl" shadow="2xl">
          <ModalHeader fontSize="2xl" fontWeight="bold" pb={2}>
            {copy.helpTitle}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8} pt={4}>
            <VStack spacing={8} align="stretch">
              <VStack spacing={6} align="stretch">
                <Card
                  variant="outline"
                  borderRadius="lg"
                  borderWidth="1px"
                  p={6}
                >
                  <VStack spacing={4} align="start">
                    <HStack spacing={3}>
                      <Box p={2} borderRadius="full" bg="blue.100">
                        <FiUser color="#3182ce" size={24} />
                      </Box>
                      <Text fontWeight="bold" color="blue.700" fontSize="lg">
                        {copy.students}
                      </Text>
                    </HStack>
                    <VStack spacing={2} align="start" pl={2}>
                      <Text fontSize="sm" color="gray.700">
                        <strong>{copy.studentCredentialsLabel}</strong>{' '}
                        {copy.studentCredentialsText}
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        <strong>{copy.studentPasswordLabel}</strong>{' '}
                        {copy.studentPasswordText}
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        <strong>{copy.studentAssistanceLabel}</strong>{' '}
                        {copy.studentAssistanceText}
                      </Text>
                    </VStack>
                  </VStack>
                </Card>

                <Card
                  variant="outline"
                  borderRadius="lg"
                  borderWidth="1px"
                  p={6}
                >
                  <VStack spacing={4} align="start">
                    <HStack spacing={3}>
                      <Box p={2} borderRadius="full" bg="green.100">
                        <FiUsers color="#22863a" size={24} />
                      </Box>
                      <Text fontWeight="bold" color="green.700" fontSize="lg">
                        {copy.teachers}
                      </Text>
                    </HStack>
                    <VStack spacing={2} align="start" pl={2}>
                      <Text fontSize="sm" color="gray.700">
                        <strong>{copy.teacherLoginLabel}</strong>{' '}
                        {copy.teacherLoginText}
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        <strong>{copy.teacherForgotLabel}</strong>{' '}
                        {copy.teacherForgotText}
                      </Text>
                      <Text fontSize="sm" color="gray.700">
                        <strong>{copy.teacherAccountLabel}</strong>{' '}
                        {copy.teacherAccountText}
                      </Text>
                    </VStack>
                  </VStack>
                </Card>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Footer */}
      <Box
        h="10%"
        bg={overlayBg}
        borderTop="1px solid"
        borderTopColor="whiteAlpha.200"
        display="flex"
        alignItems="center"
      >
        <Container maxW="6xl" px={{ base: 4, md: 8 }}>
          <VStack spacing={2} textAlign="center">
            <Box
              display="flex"
              flexDirection={{ base: 'column', md: 'row' }}
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              <Image
                src={csrf_logo}
                alt="School Logo"
                h={{ base: '20px', md: '24px' }}
              />
              <Text
                color="white"
                fontWeight="bold"
                fontSize={{ base: 'sm', md: 'base' }}
              >
                {copy.title}
              </Text>
            </Box>
            <VStack spacing={1}>
              <Text
                color="whiteAlpha.900"
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="500"
              >
                {copy.footerCopyright}
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};
